import { useMutation, useQueryClient } from "@tanstack/react-query";
import axiosInstance from "@/lib/axios";
import { toast } from "sonner";
import {
  gmailDisconnectEndpoint,
  gmailInboxAudienceEndpoint,
  gmailMessagesEndpoint,
  gmailOAuthStartEndpoint,
} from "@/config/endpoints";
import {
  GMAIL_ACCOUNT_LIST_QUERY_KEY,
  GMAIL_ACCOUNT_QUERY_KEY,
} from "./useGmailAccount";
import { GMAIL_MESSAGES_QUERY_KEY } from "./useGmailMessages";
import { GMAIL_INBOX_QUERY_KEY, inboxErrorMessage } from "./useGmailInbox";
import type { InboxAudience, SendMessagePayload } from "../types/gmail.types";

/**
 * Mutations: send (templated or raw), disconnect, start-oauth (popup).
 *
 * `useStartGmailOAuth` opens Google's consent screen in a popup and
 * resolves once the user navigates back to Xale. The server's
 * /oauth/callback redirects to APP_URL?gmail=connected which the popup
 * detects via window.location parsing — see ConnectGmailButton for the
 * full polling flow.
 */

export function useSendGmailMessage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: SendMessagePayload) => {
      const res = await axiosInstance.post(gmailMessagesEndpoint, payload);
      return res.data?.data ?? res.data;
    },
    onSuccess: () => {
      toast.success("Email queued for delivery");
      qc.invalidateQueries({ queryKey: [GMAIL_MESSAGES_QUERY_KEY] });
    },
    onError: (err: any) => {
      toast.error(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to send email — try again",
      );
    },
  });
}

export function useDisconnectGmail() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const url = gmailDisconnectEndpoint.replace(":id", String(id));
      const res = await axiosInstance.post(url);
      return res.data;
    },
    onSuccess: () => {
      toast.success("Gmail disconnected");
      qc.invalidateQueries({ queryKey: GMAIL_ACCOUNT_QUERY_KEY });
      qc.invalidateQueries({ queryKey: GMAIL_ACCOUNT_LIST_QUERY_KEY });
      qc.invalidateQueries({ queryKey: [GMAIL_MESSAGES_QUERY_KEY] });
      qc.invalidateQueries({ queryKey: [GMAIL_INBOX_QUERY_KEY] });
      qc.invalidateQueries({ queryKey: ["lead-sources"] });
    },
    onError: (err: any) => {
      toast.error(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to disconnect Gmail",
      );
    },
  });
}

/** Who may read the workspace inbox in Xale — admins only by default. */
export function useSetInboxAudience() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { accountId: number; audience: InboxAudience }) => {
      const url = gmailInboxAudienceEndpoint.replace(":id", String(vars.accountId));
      const res = await axiosInstance.put(url, { audience: vars.audience });
      return res.data;
    },
    onSuccess: (_data, vars) => {
      toast.success(
        vars.audience === "everyone"
          ? "Everyone in the workspace can now read the inbox"
          : "Only admins can read the inbox now",
      );
      qc.invalidateQueries({ queryKey: GMAIL_ACCOUNT_QUERY_KEY });
      qc.invalidateQueries({ queryKey: GMAIL_ACCOUNT_LIST_QUERY_KEY });
    },
    onError: (err) => {
      toast.error(inboxErrorMessage(err, "Couldn't change who reads the inbox"));
    },
  });
}

/**
 * Result of a Google consent popup, broadcast by the popup when it lands
 * back in Xale (GmailOAuthReturn): `connect` from Settings / Gmail, `inbox`
 * from the Inbox tab. A BroadcastChannel rather than window.opener: Google's
 * pages can sever the opener link.
 */
export const GMAIL_OAUTH_CHANNEL = "xale-gmail-oauth";

export interface GmailOAuthBroadcast {
  kind: "connect" | "inbox";
  ok: boolean;
  reason?: string;
}

/**
 * Triggers a popup-based OAuth flow. Returns when the popup closes (signal
 * from the server's /oauth/callback redirect to APP_URL?gmail=connected).
 * `inbox: true` asks for read access on top of send (server: scope=inbox).
 *
 * NOTE: The server's `/oauth/start` route returns the consent URL, but the
 * frontend only needs to navigate the popup to that URL once we have the
 * absolute redirect. We hit the endpoint to fetch the URL and then drive the
 * popup. Caller should re-fetch the account after the popup closes.
 */
export async function startGmailOAuthPopup(
  opts: { inbox?: boolean } = {},
): Promise<void> {
  let url: string | undefined;
  try {
    const res = await axiosInstance.get(gmailOAuthStartEndpoint, {
      params: opts.inbox ? { scope: "inbox" } : undefined,
    });
    url = res.data?.data?.url ?? res.data?.url;
  } catch (err) {
    // e.g. 403 "Only an admin can connect or change the workspace Gmail."
    toast.error(inboxErrorMessage(err, "Couldn't start Google sign-in. Try again."));
    return;
  }
  if (!url) {
    toast.error("Failed to start Gmail OAuth (no consent URL)");
    return;
  }

  const w = 520;
  const h = 640;
  const left = window.screenX + (window.outerWidth - w) / 2;
  const top = window.screenY + (window.outerHeight - h) / 2;
  const popup = window.open(
    url,
    "gmail-oauth",
    `width=${w},height=${h},left=${left},top=${top},menubar=no,toolbar=no`,
  );

  if (!popup) {
    toast.error("Popup blocked — allow popups and try again");
    return;
  }

  return new Promise((resolve) => {
    const timer = setInterval(() => {
      if (popup.closed) {
        clearInterval(timer);
        resolve();
      }
    }, 500);
  });
}
